import $ from 'jquery';

/**
 * Instances of the Observer class can be observed by other objects. The observed object can raise its own
 * events and observer objects can attach event handlers to the observed object. Triggering an event by
 * the observed object means the execution of all event handlers attached to that particular event.
 *
 * Event names are strings. Event handlers are functions with one argument, the data sent by the observed object.
 * Within event handlers the 'this' is mapped to the observed object.
 */
export function Observable() {
    var handlers = {};

    this.on = function (name, func) {
        if (!handlers[name]) {
            handlers[name] = [];
        }
        handlers[name].push(func);
    };

    this.trigger = function (name, data) {
        if (handlers[name] && handlers[name].length > 0) {
            for (var i = 0, l = handlers[name].length; i < l; i++) {
                handlers[name][i].call(null, data);
            }
        }
    };

    this.off = function (name, func) {
        var l = arguments.length;
        if (l === 0) {
            handlers = {};
        } else if (l === 1 && handlers[name]) {
            delete handlers[name];
        } else if (l === 2 && handlers[name] && handlers[name].length > 0) {
            for (var i = 0, k = handlers[name].length; i < k; i++) {
                if (handlers[name][i] === func) {
                    delete handlers[name][i];
                    return;
                }
            }
        }
    };
}

/**
 * A class which is a wrapper of a single value. It extends the Observable class, so it throws a 'change:value'
 * event on value changed.
 *
 * @param val The initial value of this class.
 * @param changeCallback The callback method which is invoked on value change. This can define additional effect
 * associated to the change event.
 */
export function ObservableValue(val, changeCallback) {
    var self = this;

    this.value = function () {
        if (arguments.length) {
            var newVal = arguments[0];
            if (newVal !== val) {
                val = newVal;
                self.trigger('change:value', val);
                if (typeof changeCallback === 'function') {
                    invoke(changeCallback, val);
                }
            }
        } else {
            return val;
        }
    };

    Observable.call(this);
}

export function shouldBeInitialized(widget) {
    return !widget.hasAttribute('data-uwr-ready');
}

export function markInitialized(widget) {
    widget.setAttribute('data-uwr-ready', '');
}

export function unmarkInitialized(widget) {
    widget.removeAttribute('data-uwr-ready');
}
/**
 * Invokes a function passed in as the first argument. If there are more arguments passed in, they will be used
 * as parameters for the function being invoked.
 * If the first parameter is not a function, then nothing will be done.
 *
 * @param First param is the function to invoke, any further params will be passed in to that function.
 *     If the first parameter is not a function, then nothing will be done.
 */
export function invoke(fn) {
    if (typeof fn === 'function') {
        return fn.apply(null, Array.prototype.slice.call(arguments, 1));
    }
}

export function isString(value) {
    return $.type(value) === 'string';
}

export function stringToObject(str) {
    if (isString(str)) {
        str = JSON.parse(str);
    }
    return str;
}

export function objectToString(obj) {
    if (isString(obj)) {
        return obj;
    }
    if ($.isPlainObject(obj)) {
        return JSON.stringify(obj);
    }
    return '' + obj;
}

function runOnArrayOrString(arrayOrString, callable) {
    var i;
    if (typeof arrayOrString === 'string') {
        arrayOrString = [arrayOrString];
    }
    for (i = 0; i < arrayOrString.length; i++) {
        callable.call(arrayOrString[i]);
    }
}

function isTouchExplorer() {
    return (navigator.userAgent.indexOf('MSIE') > -1 || navigator.userAgent.indexOf('rv:11.0') > -1)
            && navigator.userAgent.indexOf('Touch') > -1;
}

/**
 * This function checks if we are on a touch device (usually a tablet or a smart phone)
 */
export function isTouchDevice() {
    return isTouchExplorer() ? true : !!('ontouchstart' in window);
}

export function isIe7() {
    return isIe(7);
}

export function isIe8() {
    return isIe(8);
}

export function isIe9() {
    return isIe(9);
}

/**
 * Is the browser based on an IE version or not.
 * @param version Optional IE version string.
 * @returns {boolean}
 */
export function isIe(version, userAgentString) {
    userAgentString = userAgentString || navigator.userAgent;

    var versionString = version || '',
        match = userAgentString.match(
            new RegExp('(msie ' + versionString + '|trident.*rv[ :]' + versionString + ')', 'i')
        );
    return match !== null;
}

/**
 * @param {string} A JS reference string.
 * @return The reference string cleaned up. No javascript calls, functions and statements are allowed
 *         in this reference - not allowed characters are removed.
 */
export function sanitizeReferenceString(reference) {
    return reference.replace(/[^\w\.\[\]\'\"-]/g, '');
}

/**
 * @param {string} A JS expression string.
 * @return The reference of JS expression string.
 */
export function resolveReference(str) {
    var reference;
    if (str) {
        reference = eval(sanitizeReferenceString(str)); // eslint-disable-line
    }
    return reference;
}

/**
 * The missing signum function of Math. Value should be a number.
 */
export function sign(value) {
    return value > 0 ? 1 : value < 0 ? -1 : 0;
}

/**
 * Extract HTMLElement if it is wrapped by jQuery or it gives back the original object
 */
function extractHtmlElement(elementObject) {
    return (elementObject && elementObject instanceof $) ? elementObject.get(0) : elementObject;
}

/**
 * Get data attribute of the elem and format it.
 *
 * @param elementObject A jQuery object or a pure HTML element which data attribute should be retrieved.
 * @param name The name of the data attribute. (without data- prefix).
 * @param defaultValue The default value if no data attribute found.
 * @param formatter Optional, to format value.
 * @return The value of the data attribute or the defaultValue if no data attribute found.
 */
export function data(elementObject, name, defaultValue, formatter) {
    var elem = extractHtmlElement(elementObject),
        attributeName = 'data-' + name,
        elemData = (elem && elem.hasAttribute(attributeName))
            ? elem.getAttribute(attributeName)
            : undefined,
        value = elemData !== undefined ? elemData : defaultValue;

    return formatter ? formatter(value) : value;
}

/**
 * Get data attribute of the elem as integer.
 *
 * @param $elem The jQuery elem which data is retrieved.
 * @param name The name of the data attribute.
 * @param defaultValue The default value if no data attribute found.
 * @return The integer value of the data attribute or the defaultValue if no data attribute found.
 */
export function intData($elem, name, defaultValue) {
    return data($elem, name, defaultValue, parseInt);
}

/**
 * Get data attribute of the elem as JSON object.
 *
 * @param $elem The jQuery elem of which data is retrieved.
 * @param name The name of the data attribute.
 * @param defaultValue The default value if no data attribute found or the data is not valid JSON.
 *        It can be of any type.
 * @return The JSON object of the data attribute or the given defaultValue if no data attribute found
 *         or the data is not valid JSON.
 */
export function jsonData($elem, name, defaultValue) {
    return data($elem, name, defaultValue, function (val) {
        var json;
        try {
            json = stringToObject(val);
        /* eslint-disable no-empty */
        } catch (e) {}
        /* eslint-enable no-empty */
        if (!json) {
            json = defaultValue;
        }
        return json;
    });
}

/**
 * Resolve an object based on the reference string.
 * @param referenceString The name of the variable holding the object.
 * @param args The optional arguments array of the object provider function.
 * @return {*} The referenced object or if the referenced object is a function object then it gives back the return
 * value of that function.
 */
export function resolveReferencedObject(referenceString, args) {
    var referencedObj = resolveReference(referenceString),
        resolvedObject;
    if ($.isFunction(referencedObj)) {
        resolvedObject = referencedObj.apply(undefined, args || []);
    } else {
        // Clone so that we don't modify the client's original object
        resolvedObject = $.extend(true, {}, referencedObj);
    }
    return resolvedObject;
}

/**
 * Get the configuration from the HTML element attribute.
 *
 * @param $elem The jQuery elem of which attribute content is retrived.
 * @param attributeName The name of the data attribute (without the data- prefix).
 * @param args The optional arguments array of the configuration provider function.
 * @return {*} The JSON configuration object which is provided based on the attribute's value.
 */
export function resolveConfig($elem, attributeName, args) {
    var obj = jsonData($elem, attributeName, data($elem, attributeName)),
        config = {};

    if (obj) {
        if ($.isPlainObject(obj)) {
            config = obj;
        } else {
            config = resolveReferencedObject(String(obj), args);
        }
    }

    return config;
}

/**
 * Adds class to HTML element
 *
 * @param element The element to add class to.
 * @param className The name of the class.
 */
export function addClass(element, className) {
    if (element.classList) {
        element.classList.add(className);
    } else {
        element.className += ' ' + className;
    }
}

/**
 * Check if an HTML element has a specific class set.
 *
 * @param element The element to check the class on.
 * @param className The name of the class to check for.
 * @return The result of the check, either true or false.
 */
export function hasClass(element, className) {
    if (element.classList) {
        return element.classList.contains(className);
    }

    return new RegExp('(^| )' + className + '( |$)', 'gi').test(element.className);
}

/**
 * Remove class from HTML element
 *
 * @param element The element to remove class from.
 * @param className The name of the class.
 */
export function removeClass(element, className) {
    if (element.classList) {
        element.classList.remove(className);
    } else {
        element.className = element.className.replace(new RegExp('(^|\\b)' +
            className.split(' ').join('|') + '(\\b|$)', 'gi'), ' ').trim();
    }
}

/**
 * Copies one or more attributes from a HTML element to another if these attributes exist on the source element.
 */
export function copyAttributesIfExist(fromElement, toElement, attributeNames) {
    runOnArrayOrString(attributeNames, function copyAttribute() {
        if (fromElement.hasAttribute(this)) {
            toElement.setAttribute(this, fromElement.getAttribute(this));
        }
    });
}

/**
 * Check if an HTML element is in focus or not.
 *
 * @param element The element to check is in focus.
 * @return Is the element in focus or not.
 */
export function hasFocus(element) {
    return document.activeElement === element;
}

export function isNodeDetached(node) {
    var currentNode = node;
    while(currentNode !== null && currentNode !== document.body) {
        currentNode = currentNode.parentNode;
    }
    return currentNode !== document.body;
}

/**
 * @return The event object regardless of event subscription type
 * If you assign an event handler using the DOM0 property way (using onclick)
 * the instance of the 'event' object will be not passed to the handler.
 * In this case it should be taken from the global 'window' object
 */
export function fixEvent(event) {
    return event || window.event;
}

/**
 * Create a new event with IE fallbacks.
 * @param eventType Event type to create.
 * @returns {*} The new event object.
 */
export function createEvent(eventType, eventInit) {
    var event;
    if (typeof Event === 'function') { // Latest API
        event = new Event(eventType, eventInit);
    } else if (document.createEvent) { // Modern IE
        event = document.createEvent('Event');
        eventInit = eventInit || {};
        event.initEvent(eventType, !!eventInit.bubbles, !!eventInit.cancelable);
    } else { // IE fallback
        event = document.createEventObject();
        event.type = eventType;
    }
    return event;
}

/**
 * Fire an event on a defined HTML element using the browser's infrastructure.
 * @param eventOrType The type of the event (like change or click) or the Event object itself.
 * @param element The HTML element which is the target of the event.
 *
 * Please notice, that an already existing event (for example what you would like to forward) cannot be
 * dispatched again. You should create a new event for dispatching every time.
 *
 * Backported to UW4.
 */
export function dispatchEvent(eventOrType, element) {
    var event = typeof eventOrType === 'string' ? createEvent(eventOrType) : eventOrType;

    if (element.dispatchEvent) {
        element.dispatchEvent(event);
    } else { // before IE9 (IE8)
        try {
            element.fireEvent('on' + event.type, event);
        /* eslint-disable no-empty */
        } catch (e) {}
        /* eslint-enable no-empty */
    }
}

/**
 * An IE8 Element.prototype.addEventListener shim
 * on the specified element.
 * @param eventTypes One or more event type(s) to add listener.
 * @param element The element on which the event type should be added.
 * @param listener The listener function which should be added.
 */
export function addEventListener(eventTypes, element, listener) {
    runOnArrayOrString(eventTypes, function () {
        if (element.addEventListener) {
            element.addEventListener(this, listener);
        } else {
            element.attachEvent('on' + this, listener);
        }
    });
}

/**
 * An IE8+ Element.prototype.removeEventListener shim
 * @param eventTypes One or more event type(s) to remove listener.
 * @param element The element from which the event type should be removed.
 * @param listener The listener function which should be removed.
 */
export function removeEventListener(eventTypes, element, listener) {
    runOnArrayOrString(eventTypes, function () {
        if (element.removeEventListener) {
            element.removeEventListener(this, listener);
        } else {
            element.detachEvent('on' + this, listener);
        }
    });
}

/**
 * Forward event from an HTML element to another.
 * @param eventTypes One or more event type names to forward.
 * @param from The HTML element where the event was dispatched originally.
 * @param to The HTML element where the event is dispatched to.
 */
export function forwardEvents(eventTypes, from, to) {
    addEventListener(eventTypes, from, function (event) {
        var copiedEvent = createEvent(event.type);
        dispatchEvent(copiedEvent, to);
    });
}

/**
 * IE8 Event.target polyfill
 * @return The event's target in jQuery object regardless of browser type
 */
export function getEventTarget(event, asHtmlElement) {
    var ev = fixEvent(event);
    return asHtmlElement
        ? ev.target || ev.srcElement
        : $(ev.target || ev.srcElement);
}

/**
 * IE8 Event.prototype.stopPropagation polyfill
 * Stops the propagation of the event in a backward compatible way.
 */
export function stopPropagation(ev) {
    if (ev.stopPropagation) {
        ev.stopPropagation();
    } else {
        window.event.cancelBubble = true;
    }
}

// IE8 Event.prototype.preventDefault polyfill
export function preventDefault(event) {
    event = fixEvent(event);

    if (event.preventDefault) {
        event.preventDefault();
    } else {
        event.returnValue = false;
    }
}

/**
 * @deprecated Please use common.stopPropagation() + common.preventDefault() instead
 * Cancels the event in a backward compatible way.
 */
export function cancelEvent(ev) {
    ev.stopPropagation(); // prevent bubbling of event to any parent incl. the background
    ev.preventDefault(); // prevent the default action on event, e.g., scrolling
    ev.returnValue = false; // for backward compatibility
    return false; // for backward compatibility
}

/**
 * Generates UUID for UWR elements
 * Based on https://jsfiddle.net/briguy37/2MVFd/
 */
export function generateUUID() {
    /* eslint-disable */
    var d = new Date().getTime();
    var uuid = 'uuid-xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        var r = (d + Math.random() * 16) % 16 | 0;
        d = Math.floor(d / 16);
        return (c == 'x' ? r : (r&0x3|0x8)).toString(16);
    });
    return uuid;
    /* eslint-enable */
}

// IE8 Array.prototype.forEach polyfill
// Source: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/forEach#Polyfill
// License: CC0 1.0 https://creativecommons.org/publicdomain/zero/1.0/
export function forEach() {
    /* eslint-disable */

    if (Array.prototype.forEach) {
        return Array.prototype.forEach.apply(this, arguments);
    }

    var callback = arguments[0],
        thisArg = arguments[1],
        T,
        k,
        O,
        len,
        kValue;

    if (this === null) {
        throw new TypeError(' this is null or not defined');
    }

    O = Object(this);
    len = O.length >>> 0;

    if (typeof callback !== 'function') {
        throw new TypeError(callback + ' is not a function');
    }

    if (arguments.length > 1) {
        T = thisArg;
    }

    k = 0;

    while (k < len) {
        if (k in O) {
            kValue = O[k];
            callback.call(T, kValue, k, O);
        }
        k++;
    }
    /* eslint-enable */
}

// IE8 Array.prototype.filter polyfill
// Source: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/filter#Polyfill
// License: CC0 1.0 https://creativecommons.org/publicdomain/zero/1.0/
// eslint-disable-next-line complexity
export function filter() {
    /* eslint-disable */

    var fun = arguments[0],
        t,
        len,
        res,
        thisArg,
        i,
        val;

    if (Array.prototype.filter) {
        return Array.prototype.filter.apply(this, arguments);
    }

    if (this === void 0 || this === null) {
        throw new TypeError();
    }

    t = Object(this);
    len = t.length >>> 0;
    if (typeof fun !== 'function') {
        throw new TypeError();
    }

    res = [];
    thisArg = arguments.length >= 2 ? arguments[1] : void 0;
    for (i = 0; i < len; i++) {
        if (i in t) {
            val = t[i];

            if (fun.call(thisArg, val, i, t)) {
                res.push(val);
            }
        }
    }

    return res;
    /* eslint-enable */
}

// IE8 Element.prototype.textContent polyfill
export function textContent(element, text) {
    if (arguments.length === 1) {
        return element.textContent || element.innerText;
    }

    if (element.textContent) {
        element.textContent = text;
    } else {
        element.innerText = text;
    }

}

// IE8 String.prototype.trim polyfill
// Source: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/Trim#Polyfill
// License: CC0 1.0 https://creativecommons.org/publicdomain/zero/1.0/
export function trim() {
    if (String.prototype.trim) {
        return String.prototype.trim.apply(this, arguments);
    }

    return this.replace(/^[\s\uFEFF\xA0]+|[\s\uFEFF\xA0]+$/g, '');
}

/** IE8 Array.prototype.indexOf polyfill
 * Source: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/indexOf#Polyfill
 * License: CC0 1.0 https://creativecommons.org/publicdomain/zero/1.0/
 */
// eslint-disable-next-line complexity
export function indexOf(searchElement, fromIndex) {
    if (Array.prototype.indexOf) {
        return Array.prototype.indexOf.call(this, searchElement, fromIndex);
    }

    /* eslint-disable */
    var k;

    // 1. Let o be the result of calling ToObject passing
    //    the this value as the argument.
    if (this == null) {
        throw new TypeError('"this" is null or not defined');
    }

    var o = Object(this);

    // 2. Let lenValue be the result of calling the Get
    //    internal method of o with the argument "length".
    // 3. Let len be ToUint32(lenValue).
    var len = o.length >>> 0;

    // 4. If len is 0, return -1.
    if (len === 0) {
        return -1;
    }

    // 5. If argument fromIndex was passed let n be
    //    ToInteger(fromIndex); else let n be 0.
    var n = fromIndex | 0;

    // 6. If n >= len, return -1.
    if (n >= len) {
        return -1;
    }

    // 7. If n >= 0, then Let k be n.
    // 8. Else, n<0, Let k be len - abs(n).
    //    If k is less than 0, then let k be 0.
    k = Math.max(n >= 0 ? n : len - Math.abs(n), 0);

    // 9. Repeat, while k < len
    while (k < len) {
        // a. Let Pk be ToString(k).
        //   This is implicit for LHS operands of the in operator
        // b. Let kPresent be the result of calling the
        //    HasProperty internal method of o with argument Pk.
        //   This step can be combined with c
        // c. If kPresent is true, then
        //    i.  Let elementK be the result of calling the Get
        //        internal method of o with the argument ToString(k).
        //   ii.  Let same be the result of applying the
        //        Strict Equality Comparison Algorithm to
        //        searchElement and elementK.
        //  iii.  If same is true, return k.
        if (k in o && o[k] === searchElement) {
            return k;
        }
        k++;
    }
    return -1;
    /* eslint-enable */
}

// IE8 Array.prototype.map polyfill
// Source: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/map#Polyfill
// License: CC0 1.0 https://creativecommons.org/publicdomain/zero/1.0/
// Production steps of ECMA-262, Edition 5, 15.4.4.19
// Reference: http://es5.github.io/#x15.4.4.19
export function map(callback, thisArg) {
    /* eslint-disable */
    if (Array.prototype.map) {
        return Array.prototype.map.apply(this, arguments);
    }

    var T, A, k;

    if (this == null) {
        throw new TypeError(' this is null or not defined');
    }

    // 1. Let O be the result of calling ToObject passing the |this|
    //    value as the argument.
    var O = Object(this);

    // 2. Let lenValue be the result of calling the Get internal
    //    method of O with the argument "length".
    // 3. Let len be ToUint32(lenValue).
    var len = O.length >>> 0;

    // 4. If IsCallable(callback) is false, throw a TypeError exception.
    // See: http://es5.github.com/#x9.11
    if (typeof callback !== 'function') {
        throw new TypeError(callback + ' is not a function');
    }

    // 5. If thisArg was supplied, let T be thisArg; else let T be undefined.
    if (arguments.length > 1) {
        T = thisArg;
    }

    // 6. Let A be a new array created as if by the expression new Array(len)
    //    where Array is the standard built-in constructor with that name and
    //    len is the value of len.
    A = new Array(len);

    // 7. Let k be 0
    k = 0;

    // 8. Repeat, while k < len
    while (k < len) {

        var kValue, mappedValue;

        // a. Let Pk be ToString(k).
        //   This is implicit for LHS operands of the in operator
        // b. Let kPresent be the result of calling the HasProperty internal
        //    method of O with argument Pk.
        //   This step can be combined with c
        // c. If kPresent is true, then
        if (k in O) {

            // i. Let kValue be the result of calling the Get internal
            //    method of O with argument Pk.
            kValue = O[k];

            // ii. Let mappedValue be the result of calling the Call internal
            //     method of callback with T as the this value and argument
            //     list containing kValue, k, and O.
            mappedValue = callback.call(T, kValue, k, O);

            // iii. Call the DefineOwnProperty internal method of A with arguments
            // Pk, Property Descriptor
            // { Value: mappedValue,
            //   Writable: true,
            //   Enumerable: true,
            //   Configurable: true },
            // and false.

            // In browsers that support Object.defineProperty, use the following:
            // Object.defineProperty(A, k, {
            //   value: mappedValue,
            //   writable: true,
            //   enumerable: true,
            //   configurable: true
            // });

            // For best browser support, use the following:
            A[k] = mappedValue;
        }
        // d. Increase k by 1.
        k++;
    }

    // 9. return A
    return A;
    /* eslint-enable */
}

/**
 * Clones an object with omiting provided properties
 * @param {object} object
 * @param {string[]} keys
 * @returns {object}
 */
export function omit(object, keys) {
    return Object.keys(object).reduce(function (result, key) {
        if (!keys || keys.indexOf(key) < 0) {
            result[key] = object[key];
        }
        return result;
    }, {});
}

/**
 * Returns true if parameter can be iterated.
 * @param {*} obj
 */
function canBeIterated(obj) {
    return Array.isArray(obj) || obj instanceof $ || obj instanceof NodeList;
}

/**
 * Returns the array of nodes based on given selector or object.
 * @param {*} selectorOrObject CSS Selector string or object which can be Node or JQuery element or a collection of them
 * @returns {Array} an array of elements which can be iterated
 */
function getNodeArray(selectorOrObject) {
    var nodeArray = [];
    if (typeof selectorOrObject === 'string') {
        selectorOrObject = document.querySelectorAll(selectorOrObject);
    }
    if (canBeIterated(selectorOrObject)) {
        forEach.call(selectorOrObject, function (elem) {
            if (elem instanceof Node) {
                nodeArray.push(elem);
            } else if (elem instanceof $) {
                nodeArray.push(elem[0]);
            }
        });
    } else if (selectorOrObject instanceof $ || selectorOrObject instanceof Node) {
        nodeArray = [selectorOrObject];
    }
    return nodeArray;
}

/**
 * Normalize optional selectorOrCallback and callback parameters and returns the nodeArray and callback.
 * @param {*} defaultSelector is the default selector, if selector is not defined.
 * @param {*} Widget widget constructor
 * @param {*} selectorOrCallback is an optional parameter which can be a CSS Selector string
 * or a callback function if callback parameter is not defined.
 * @param {*} callback is an optional callback function if selectorOrCallback is CSS Selector string.
 */
export function initWidget(defaultSelector, Widget, selectorOrCallback, callback) {
    var callbackWithoutSelector = callback === undefined && typeof selector === 'function',
        selector = selectorOrCallback === undefined || callbackWithoutSelector ? defaultSelector : selectorOrCallback,
        widgets = getNodeArray(selector).map(function (node) {
            return new Widget(node).init();
        });

    invoke(callbackWithoutSelector ? selectorOrCallback : callback);
    return widgets.length === 1 ? widgets[0] : widgets;
}

/**
 * Moves markup element out from wrapper container and place it before the container.
 *
 * @param markup {Node} is a living element in DOM
 * @param container {Node} is a living element in DOM
 */
export function unwrapMarkup(markup, container) {
    if (container && container.parentNode) {
        container.parentNode.insertBefore(markup, container);
        container.parentNode.removeChild(container);
    }
}

/**
 * Returns the size of an element and its position relative to the viewport in an object if the element is bound to a
 * DOM element. Otherwise method returns undefined.
 * @param {*} el is a DOM element
 */
export function getBoundingClientRect(el) {
    var boundingClientRect;
    try {
        boundingClientRect = el.getBoundingClientRect();
    /* eslint-disable no-empty */
    } catch (e) { }
    /* eslint-enable no-empty */
    return boundingClientRect;
}



//////////////////
// WEBPACK FOOTER
// ./shared/modules/common.js
// module id = 1
// module chunks = main